import React, { useEffect, useState } from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon, ArrowPathIcon, LinkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { googleCalendarService, GoogleCalendarStatus } from '../../services/googleCalendarService';

interface GoogleAuthButtonProps {
  onAuthChange?: (isAuthenticated: boolean) => void;
  className?: string;
}

/**
 * Google Naptár összekapcsolása (felhasználónkénti OAuth).
 * Az összekapcsolás egyszeri: a szerver tárolja a refresh tokent, a foglalások a
 * szervező saját naptárában jönnek létre Meet linkkel.
 */
const GoogleAuthButton: React.FC<GoogleAuthButtonProps> = ({ onAuthChange, className = '' }) => {
  const [status, setStatus] = useState<GoogleCalendarStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    const st = await googleCalendarService.status();
    setStatus(st);
    onAuthChange?.(st.connected);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // Visszatérés a Google consent-ről
    const params = new URLSearchParams(window.location.search);
    const g = params.get('google');
    if (g === 'connected') toast.success('Google Naptár összekapcsolva');
    if (g === 'denied') toast.error('A Google hozzáférést nem engedélyezted');
    if (g === 'error') toast.error('Nem sikerült az összekapcsolás');
    if (g) {
      params.delete('google');
      window.history.replaceState({}, '', `${window.location.pathname}${params.toString() ? `?${params}` : ''}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setBusy(true);
    try { await googleCalendarService.connect(); } catch (e: any) { toast.error(e?.message || 'Nem sikerült elindítani'); setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await googleCalendarService.disconnect();
      toast.success('Google Naptár leválasztva');
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Nem sikerült leválasztani');
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className={`flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 ${className}`}>
        <ArrowPathIcon className="w-4 h-4 animate-spin" /> Ellenőrzés…
      </div>
    );
  }

  if (status && !status.configured) {
    return (
      <div className={`flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-sm ${className}`}>
        <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
        <div className="text-amber-800 dark:text-amber-300 min-w-0">
          {status.error
            ? <>A Google-állapot lekérése nem sikerült: <span className="font-mono text-xs break-all">{status.error}</span></>
            : <>A Google-integráció a szerveren még nincs beállítva (OAuth client: GOOGLE_OAUTH_CLIENT_ID / _SECRET a functions/.env-ben, majd deploy).</>}
          {' '}Addig a „Saját fix link” opcióval működik az online meeting.
        </div>
        <button type="button" onClick={load} className="crm-btn crm-btn-secondary h-7 text-xs ml-auto flex-shrink-0">Újra</button>
      </div>
    );
  }

  if (status?.connected) {
    return (
      <div className={`flex flex-wrap items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/30 ${className}`}>
        <CheckCircleIcon className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
        <div className="text-sm min-w-0 flex-1">
          <span className="font-medium text-gray-900 dark:text-gray-50">Összekapcsolva</span>
          {status.email && <span className="text-gray-600 dark:text-gray-400"> · {status.email}</span>}
        </div>
        <button type="button" onClick={disconnect} disabled={busy} className="crm-btn crm-btn-secondary h-8 text-xs">
          Leválasztás
        </button>
      </div>
    );
  }

  return (
    <div className={`flex flex-wrap items-center gap-2 p-3 rounded-lg bg-[var(--crm-bg)] border border-[var(--crm-border)] ${className}`}>
      <div className="text-sm text-gray-600 dark:text-gray-400 min-w-0 flex-1">
        {status?.needsReconnect ? 'A hozzáférés lejárt – kapcsold össze újra.' : 'Nincs összekapcsolva. A foglalások a saját Google Naptáradba kerülnek, Meet linkkel.'}
      </div>
      <button type="button" onClick={connect} disabled={busy} className="crm-btn crm-btn-primary h-8 text-xs">
        <LinkIcon className="w-3.5 h-3.5" />
        Google Naptár összekapcsolása
      </button>
    </div>
  );
};

export default GoogleAuthButton;
